import React, { useState } from "react";
import { Sparkles, RefreshCw, Loader2, Clock, Briefcase, GraduationCap, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Markdown } from "@/components/common/Markdown";
import { DetailLevel } from "@/components/common/DetailLevel";
import { aiApi, getAiDetail, setAiDetail } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

const TYPES = [
    { key: "one_min", icon: Clock },
    { key: "five_min", icon: Clock },
    { key: "fifteen_min", icon: Clock },
    { key: "executive", icon: Briefcase },
    { key: "student", icon: GraduationCap },
    { key: "action", icon: ListChecks },
];

const LANGS = ["auto", "English", "German", "Croatian", "Spanish", "French", "Italian"];

export const SummariesPanel = ({ docId }) => {
    const { t } = useTranslation();
    const [active, setActive] = useState("one_min");
    const [lang, setLang] = useState("auto");
    const [detail, setDetail] = useState(getAiDetail());
    const [cache, setCache] = useState({}); // key `${type}:${lang}:${detail}` -> content
    const [loading, setLoading] = useState(false);

    const cacheKey = `${active}:${lang}:${detail}`;

    const load = async (type, language, dtl, refresh = false) => {
        const key = `${type}:${language}:${dtl}`;
        if (cache[key] && !refresh) return;
        setLoading(true);
        try {
            const data = await aiApi.summary(docId, type, language, refresh, dtl);
            setCache((c) => ({ ...c, [key]: data.content }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("summaries.error"));
        } finally {
            setLoading(false);
        }
    };

    const onSelectType = (type) => {
        setActive(type);
        load(type, lang, detail);
    };
    const onSelectLang = (language) => {
        setLang(language);
        load(active, language, detail);
    };
    const onSelectDetail = (dtl) => {
        setDetail(dtl); setAiDetail(dtl);
        load(active, lang, dtl);
    };

    const content = cache[cacheKey];

    return (
        <div className="flex h-full flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
                <div className="flex items-center gap-2 text-t1">
                    <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <span className="font-sans-display text-sm font-semibold">{t("summaries.title")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <DetailLevel value={detail} onChange={onSelectDetail} compact />
                    <Select value={lang} onValueChange={onSelectLang}>
                        <SelectTrigger data-testid="summary-lang-select" className="h-8 w-[100px] bg-surface-2 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {LANGS.map((l) => <SelectItem key={l} value={l}>{l === "auto" ? t("summaries.auto") : l}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="flex flex-wrap gap-1.5 border-b border-border px-3 py-2.5">
                {TYPES.map((ty) => (
                    <button
                        key={ty.key}
                        data-testid={`summary-type-${ty.key}`}
                        onClick={() => onSelectType(ty.key)}
                        className={cn(
                            "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs transition-colors",
                            active === ty.key ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-surface-2 text-t3 hover:text-t1"
                        )}
                    >
                        <ty.icon className="h-3.5 w-3.5" /> {t(`summaries.types.${ty.key}`)}
                    </button>
                ))}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4" data-testid="summary-content">
                {loading && !content ? (
                    <div className="space-y-3">
                        <Skeleton className="h-4 w-2/3 bg-surface-3" />
                        <Skeleton className="h-4 w-full bg-surface-3" />
                        <Skeleton className="h-4 w-full bg-surface-3" />
                        <Skeleton className="h-4 w-5/6 bg-surface-3" />
                        <Skeleton className="h-4 w-3/4 bg-surface-3" />
                    </div>
                ) : content ? (
                    <div className="max-w-[68ch]">
                        <Markdown>{content}</Markdown>
                        <div className="mt-5">
                            <Button data-testid="summary-regenerate-button" variant="secondary" size="sm" className="gap-2 bg-surface-3/60" disabled={loading} onClick={() => load(active, lang, detail, true)}>
                                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {t("summaries.regenerate")}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex h-full flex-col items-center justify-center py-10 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]">
                            <Sparkles className="h-6 w-6" />
                        </div>
                        <p className="mt-4 max-w-xs text-sm text-t3">{t("summaries.empty")}</p>
                        <Button data-testid="summary-generate-button" className="mt-4 gap-2" onClick={() => load(active, lang, detail)}>
                            <Sparkles className="h-4 w-4" /> {t("summaries.generate", { label: t(`summaries.types.${active}`) })}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
